import { useQuery } from '@tanstack/react-query'
import React, { ReactElement, useState } from 'react'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import GoogleCalendar2026 from 'src/assets/svgs/GoogleCalendar2026.svg'
import GoogleCalendarIntegrationModal from 'src/component-modal/GoogleCalendarIntegrationModal/GoogleCalendarIntegrationModal'
import GoogleWorkspaceIntegrationManageModal from 'src/component-modal/GoogleWorkspaceIntegrationManageModal/GoogleWorkspaceIntegrationManageModal'
import GoogleWorkspaceIntegrationCard from 'src/feature/GoogleWorkspaceIntegrations/GoogleWorkspaceIntegrationCard'
import { useTranslate } from 'src/localization/Trns'
import {
  calendarIntegrationQueries,
  useDisconnectGoogleCalendarIntegration,
} from 'src/queries/organizations/calendarIntegrations/calendarIntegrations.queries'

type GoogleCalendarIntegrationCardProps = {
  organizationId: string
  canManage: boolean
  locale?: string
}

export default function GoogleCalendarIntegrationCard({
  organizationId,
  canManage,
  locale = 'en-US',
}: GoogleCalendarIntegrationCardProps): ReactElement {
  const { analytics } = useAnalyticsContext()
  const translate = useTranslate()
  const [showSetup, setShowSetup] = useState(false)
  const [showManage, setShowManage] = useState(false)
  const integrationQuery = useQuery(
    calendarIntegrationQueries.google(organizationId),
  )
  const disconnect = useDisconnectGoogleCalendarIntegration({
    onSuccess: () => {
      setShowManage(false)
      analytics.track('calendar integration', {
        action: 'disconnect_succeeded',
        provider: 'GOOGLE',
      })
    },
  })

  const integration = integrationQuery.data
  const openSetup = () => {
    setShowSetup(true)
    analytics.track('calendar integration', {
      action: 'setup_started',
      provider: 'GOOGLE',
      status: integration?.status,
    })
  }

  const openManage = () => {
    disconnect.reset()
    setShowManage(true)
    analytics.track('calendar integration', {
      action: 'manage_opened',
      provider: 'GOOGLE',
    })
  }

  return (
    <>
      <GoogleWorkspaceIntegrationCard
        name={translate('Google Calendar')}
        description={translate(
          'Use calendar context to generate accurate memos and suggest attendees for business expenses.',
        )}
        icon={<img src={GoogleCalendar2026} alt="" />}
        status={integration?.status}
        canManage={canManage}
        onSetup={openSetup}
        onManage={openManage}
        loading={integrationQuery.isLoading}
        error={integrationQuery.isError || !integration}
        onRetry={() => integrationQuery.refetch()}
      />
      {integration && (
        <>
          <GoogleCalendarIntegrationModal
            isOpen={showSetup}
            onClose={() => setShowSetup(false)}
            organizationId={organizationId}
            integration={integration}
          />
          <GoogleWorkspaceIntegrationManageModal
            isOpen={showManage}
            onClose={() => setShowManage(false)}
            integrationName={translate('Google Calendar')}
            workspaceDomain={integration.workspaceDomain}
            connectedAt={integration.connectedAt}
            lastVerifiedAt={integration.lastVerifiedAt}
            locale={locale}
            onVerifyAgain={() => {
              setShowManage(false)
              openSetup()
            }}
            onDisconnect={() => disconnect.mutate({ orgId: organizationId })}
            disconnectPending={disconnect.isPending}
            disconnectError={disconnect.isError}
          />
        </>
      )}
    </>
  )
}
